import React, { useState, useEffect, useRef } from "react";
import Reveal from "@/components/Reveal";
import NeonButton from "@/components/NeonButton";
import GameMenuButton from "@/components/GameMenuButton";
import PlanVisitDialog from "@/components/PlanVisitDialog";
import EnterTransition from "@/components/EnterTransition";
import { MapPin, Sparkles, ArrowDown } from "lucide-react";

// Real Replay Arcade & Lounge renders
const IMG = {
  HERO_BG: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/zfce4w4g_IMG_8341.JPG",
  VHS_SHELVES: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/jlfdkggx_IMG_8350.JPG",
  ARCADE_REVEAL: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/9cpjwmbx_IMG_8336.JPG",
  TERMINATOR: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/ie6sl32q_IMG_8353.JPG",
  RHYTHM: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/yzompcnh_IMG_8339.JPG",
  CLAW: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/frt95w40_IMG_8344.JPG",
  PINBALL_BAR: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/c4fa198i_IMG_8349.JPG",
  LOUNGE: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/9owft5qf_IMG_8346.JPG",
  OLD_SCHOOL_SHOP: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/4hv9nkej_IMG_8348.JPG",
  AIR_HOCKEY: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/bwbpk3ax_IMG_8335.JPG",
  STRABANE_BOOTH: "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/1oiwhmib_IMG_8352.JPG",
};

const HEX_PINK = "#FF007A";
const HEX_CYAN = "#00F0FF";
const HEX_PURPLE = "#B026FF";

// Hero ===========================================================
const Hero = ({ onPlan, onEnterArcade, onContinue }) => {
  return (
    <section
      id="hero"
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Layered background */}
      <div className="absolute inset-0 grid-bg radial-fade opacity-60" />
      <div
        className="absolute inset-0 opacity-30 hero-ambient"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 30%, rgba(176,38,255,0.45), transparent 55%), radial-gradient(ellipse at 80% 75%, rgba(255,0,122,0.35), transparent 55%), radial-gradient(ellipse at 60% 20%, rgba(0,240,255,0.25), transparent 60%)",
          backgroundSize: "200% 200%, 200% 200%, 200% 200%",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `url('${IMG.HERO_BG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,5,16,0.55) 0%, rgba(10,5,16,0.35) 40%, rgba(10,5,16,0.85) 100%)",
        }}
      />

      {/* Top nav */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <a href="#hero" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/5dx73wcq_IMG_8068.JPG"
            alt="Replay Arcade & Lounge"
            className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover shadow-[0_0_24px_rgba(176,38,255,0.7)] ring-1 ring-white/10 transition-transform group-hover:scale-110"
          />
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.4em] text-white/80">
            Arcade <span className="text-[#FF007A]">&amp;</span> Lounge <span className="text-white/30">◉</span> Strabane
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8 font-mono text-[10px] uppercase tracking-[0.32em] text-white/60">
          <a href="#experience" className="hover:text-white transition-colors" data-testid="nav-experience">Experience</a>
          <a href="#inside" className="hover:text-white transition-colors" data-testid="nav-inside">Inside</a>
          <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="#location" className="hover:text-white transition-colors" data-testid="nav-location">Location</a>
        </nav>
        <button
          onClick={onPlan}
          data-testid="nav-plan-visit"
          className="hidden sm:inline-flex font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] border border-[#00F0FF]/50 px-4 py-2 hover:bg-[#00F0FF] hover:text-[#0A0510] transition-colors"
        >
          Plan Your Visit
        </button>
      </header>

      {/* Hero copy */}
      <div className="relative z-10 px-6 md:px-12 pt-6 sm:pt-10 pb-28">
        <div className="max-w-5xl mx-auto text-center flex flex-col items-center">
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-10">
              <span className="h-px w-10 bg-[#FF007A]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A]">
                A hidden world. Press start.
              </span>
              <span className="h-px w-10 bg-[#FF007A]" />
            </div>
          </Reveal>

          {/* Big centered logo */}
          <Reveal delay={80}>
            <div className="relative mx-auto mb-8 sm:mb-10">
              <div className="absolute inset-0 -m-10 rounded-full bg-[radial-gradient(ellipse,_rgba(176,38,255,0.45),_transparent_60%)] blur-2xl" aria-hidden="true" />
              <div className="absolute inset-0 -m-6 rounded-full bg-[radial-gradient(ellipse,_rgba(0,240,255,0.25),_transparent_60%)] blur-xl" aria-hidden="true" />
              <img
                src="https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/5dx73wcq_IMG_8068.JPG"
                alt="Replay Arcade & Lounge"
                className="relative h-44 w-44 sm:h-56 sm:w-56 md:h-64 md:w-64 rounded-full object-cover ring-1 ring-white/15 shadow-[0_0_60px_rgba(176,38,255,0.6),0_0_120px_rgba(0,240,255,0.25)] flicker-slow drift-subtle"
                data-testid="hero-logo"
              />
            </div>
          </Reveal>

          <Reveal delay={200}>
            <h1
              data-text="New Memories Loading..."
              className="glitch-text font-display font-black text-[12vw] sm:text-[10vw] md:text-[8.5vw] lg:text-[8rem] xl:text-[9rem] leading-[0.92] tracking-tighter hero-glow text-white"
            >
              New Memories
              <br />
              <span className="neon-pink flicker-slow">Loading</span>
              <span className="neon-cyan">...</span>
            </h1>
          </Reveal>

          <Reveal delay={340}>
            <p className="mt-10 max-w-xl mx-auto text-lg sm:text-xl text-[#C9C0D6] leading-relaxed font-body">
              You don&rsquo;t find this place.
              <br />
              <span className="text-white">You step into it.</span>
            </p>
          </Reveal>

          <Reveal delay={500}>
            <div
              className="mt-12 flex flex-col items-center gap-4"
              data-testid="game-menu"
            >
              <GameMenuButton
                variant="cyan"
                onClick={onEnterArcade}
                hint="Begin the experience"
                data-testid="hero-new-game"
              >
                New Game
              </GameMenuButton>
              <GameMenuButton
                variant="pink"
                onClick={onContinue}
                hint="Skip the intro"
                data-testid="hero-continue"
              >
                Continue
              </GameMenuButton>
              <button
                onClick={onPlan}
                data-testid="hero-plan-visit"
                className="mt-3 group inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 hover:text-white transition-colors"
              >
                <span className="h-px w-6 bg-white/30 group-hover:bg-[#FF007A] transition-colors" />
                or plan your visit
                <span className="h-px w-6 bg-white/30 group-hover:bg-[#FF007A] transition-colors" />
              </button>
            </div>
          </Reveal>

          <Reveal delay={680}>
            <div className="mt-12 inline-flex items-center gap-3 border border-[#FF007A]/40 px-4 py-2.5" data-testid="opening-soon">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF007A] shadow-[0_0_10px_#FF007A] flicker-slow" />
              <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A]">
                Opening Soon
              </span>
            </div>
          </Reveal>

          <Reveal delay={780}>
            <p
              className="mt-6 font-mono text-[11px] sm:text-xs uppercase tracking-[0.5em] text-white/55 calm-breath"
              data-testid="signature-line"
            >
              ▸ Press Start on Real Life
            </p>
          </Reveal>

          <Reveal delay={720}>
            <div className="mt-16 flex items-center justify-center gap-6 text-white/50">
              <ArrowDown className="h-4 w-4 animate-bounce text-[#00F0FF]" strokeWidth={1.5} />
              <span className="font-mono text-[10px] uppercase tracking-[0.4em]">
                Keep going. There is more.
              </span>
              <ArrowDown className="h-4 w-4 animate-bounce text-[#00F0FF]" strokeWidth={1.5} />
            </div>
          </Reveal>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-y border-white/5 bg-[#0A0510]/60 backdrop-blur-sm py-3 overflow-hidden">
        <div className="marquee flex whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.4em] text-white/40">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex">
              {[
                "Pacman ▸",
                "Street Fighter ▸",
                "Outrun ▸",
                "Galaga ▸",
                "Tekken ▸",
                "Donkey Kong ▸",
                "Time Crisis ▸",
                "Daytona ▸",
                "Mortal Kombat ▸",
                "Mario Kart ▸",
                "Pinball ▸",
                "Crazy Taxi ▸",
              ].map((g, idx) => (
                <span key={idx} className="mx-6 inline-block">
                  <span className={idx % 3 === 0 ? "text-[#FF007A]" : idx % 3 === 1 ? "text-[#00F0FF]" : "text-[#B026FF]"}>{g}</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Step Inside ====================================================
const StepInside = ({ onEnterArcade }) => {
  return (
    <section
      id="step-inside"
      className="relative px-6 md:px-12 py-24 md:py-32 overflow-hidden"
      data-testid="step-inside-section"
    >
      <div className="section-vignette" />
      <div className="absolute inset-0 grid-bg opacity-15 radial-fade pointer-events-none" />

      <div className="relative max-w-3xl mx-auto text-center">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-8">
            ◉ Step Inside
          </p>
        </Reveal>

        <Reveal delay={120}>
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[1.05]">
            Looks like a <span className="text-white">video store</span>.
          </h2>
        </Reveal>

        <Reveal delay={240}>
          <p className="mt-6 font-display text-2xl sm:text-4xl md:text-5xl font-black tracking-tighter">
            Feels like <span className="neon-pink">something else</span>.
          </p>
        </Reveal>

        <Reveal delay={400}>
          <p className="mt-10 font-display text-xl sm:text-3xl md:text-4xl">
            <span className="neon-cyan">Find the entrance.</span>{" "}
            <span className="text-white">Start the game.</span>
          </p>
        </Reveal>

        <Reveal delay={560}>
          <button
            onClick={onEnterArcade}
            data-testid="step-inside-enter"
            className="mt-12 group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.4em] text-white/65 hover:text-[#00F0FF] transition-colors"
          >
            <span className="h-px w-8 bg-white/30 group-hover:bg-[#00F0FF] transition-colors" />
            Begin
            <span className="h-px w-8 bg-white/30 group-hover:bg-[#00F0FF] transition-colors" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

// Experience narrative ============================================
const ExperienceLine = ({ children, color = "white", indent = 0, delay = 0, big = false }) => {
  const colorClass =
    color === "pink" ? "neon-pink" : color === "cyan" ? "neon-cyan" : color === "purple" ? "neon-purple" : "text-white";
  return (
    <Reveal delay={delay}>
      <p
        style={{ paddingLeft: `${indent}rem` }}
        className={`font-display ${big ? "text-3xl sm:text-5xl" : "text-xl sm:text-2xl md:text-3xl"} ${colorClass} leading-[1.25] tracking-tight`}
      >
        {children}
      </p>
    </Reveal>
  );
};

const Experience = () => {
  const sectionRef = useRef(null);

  // One-time gentle pulse: fires once when the section first enters view.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    let fired = false;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !fired) {
            fired = true;
            // Small intentional delay so the moment lands after the user has settled in.
            setTimeout(() => {
              el.classList.add("exp-pulse-once");
              setTimeout(() => el.classList.remove("exp-pulse-once"), 500);
            }, 1800);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="experience"
      className="relative px-6 md:px-12 py-32 md:py-48 overflow-hidden"
      data-testid="experience-section"
    >
      <div className="section-vignette" />
      <div className="absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#B026FF]/15 blur-[140px]" />
      <div className="absolute right-0 bottom-0 h-96 w-96 rounded-full bg-[#FF007A]/10 blur-[140px]" />

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 max-w-7xl mx-auto">
        <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-6">
              ◉ 01 / The Experience
            </p>
            <div className="aspect-[4/5] relative overflow-hidden border border-white/10">
              <img
                src={IMG.VHS_SHELVES}
                alt="The front. Shelves. Soft light."
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0510]/85 via-[#0A0510]/30 to-transparent" />
              <div className="absolute inset-0 crt-scanlines opacity-40" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/80">
                  &gt; The front
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A] flicker">
                  ◉ Old school
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8 flex flex-col gap-9 sm:gap-11">
          <ExperienceLine>You walk into something familiar.</ExperienceLine>
          <ExperienceLine indent={1} delay={80}>Shelves. Soft light.</ExperienceLine>
          <ExperienceLine indent={1.5} delay={140}>That feeling you haven&rsquo;t quite felt in years.</ExperienceLine>
          <ExperienceLine indent={0.5} delay={220}>A place that reminds you of simpler times.</ExperienceLine>

          <div className="my-2 neon-divider" />

          <ExperienceLine color="purple" delay={60}>But something feels different.</ExperienceLine>
          <ExperienceLine indent={1} delay={120}>There&rsquo;s more to it.</ExperienceLine>
          <ExperienceLine indent={2} delay={180}>You notice it.</ExperienceLine>
          <ExperienceLine indent={3} delay={240}>You move closer.</ExperienceLine>
          <ExperienceLine indent={2} delay={300} color="cyan">And then you find it.</ExperienceLine>

          <Reveal delay={120}>
            <div className="relative my-6 aspect-[16/10] overflow-hidden border border-[#FF007A]/30">
              <img
                src={IMG.ARCADE_REVEAL}
                alt="The arcade revealed"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0510] via-transparent to-transparent" />
              <div className="absolute inset-0 crt-scanlines opacity-30" />
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A] flicker">
                  ◉ The reveal
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/70">
                  &gt; Step through
                </p>
              </div>
            </div>
          </Reveal>

          <div className="my-2 neon-divider" />

          <ExperienceLine color="pink" big delay={60}>Not everything is what it seems.</ExperienceLine>
          <ExperienceLine indent={1} delay={140}>You step through.</ExperienceLine>
          <ExperienceLine indent={1} delay={220} color="cyan">And everything changes.</ExperienceLine>

          <Reveal delay={300}>
            <p className="mt-4 font-display text-2xl sm:text-3xl">
              <span className="neon-pink">Neon lights.</span>{" "}
              <span className="neon-cyan">Sound.</span>{" "}
              <span className="neon-purple">Energy.</span>
            </p>
          </Reveal>

          <ExperienceLine delay={120}>The kind of place you don&rsquo;t just visit.</ExperienceLine>
          <ExperienceLine delay={180} indent={1} color="pink">You feel.</ExperienceLine>

          <Reveal delay={260}>
            <p className="mt-8 font-display text-4xl sm:text-6xl font-black tracking-tighter">
              Welcome to <span className="neon-pink flicker-slow">Replay</span>.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// What's Inside ===================================================
const InsideCard = ({ title, body, accent = "pink", delay = 0, mono }) => {
  const accentClass =
    accent === "cyan" ? "border-[#00F0FF]/40 hover:shadow-[0_0_30px_rgba(0,240,255,0.25)]" :
    accent === "purple" ? "border-[#B026FF]/40 hover:shadow-[0_0_30px_rgba(176,38,255,0.25)]" :
    "border-[#FF007A]/40 hover:shadow-[0_0_30px_rgba(255,0,122,0.25)]";
  const accentText =
    accent === "cyan" ? "neon-cyan" : accent === "purple" ? "neon-purple" : "neon-pink";
  return (
    <Reveal delay={delay}>
      <div className={`relative p-7 sm:p-8 border ${accentClass} bg-[#130A1D]/60 backdrop-blur-sm transition-shadow group`}>
        <div className="absolute top-0 left-0 h-3 w-3 border-t border-l border-current opacity-60" />
        <div className="absolute bottom-0 right-0 h-3 w-3 border-b border-r border-current opacity-60" />
        <p className={`font-mono text-[10px] uppercase tracking-[0.4em] ${accentText} mb-5`}>{mono}</p>
        <h4 className={`font-display text-2xl sm:text-3xl font-bold ${accentText} leading-tight`}>{title}</h4>
        <p className="mt-4 text-[#C9C0D6] leading-relaxed text-base">{body}</p>
      </div>
    </Reveal>
  );
};

const WhatsInside = () => {
  return (
    <section
      id="inside"
      className="relative px-6 md:px-12 py-28 md:py-40 overflow-hidden"
      data-testid="inside-section"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A] mb-6">
            ◉ 02 / What&rsquo;s Inside
          </p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter max-w-3xl">
            Once you&rsquo;re in, <span className="neon-pink">you&rsquo;re in.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Big image card */}
          <Reveal className="lg:col-span-7">
            <div className="relative aspect-[16/10] overflow-hidden border border-white/10">
              <img
                src={IMG.TERMINATOR}
                alt="Rows of arcade machines, classic and modern"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0510] via-transparent to-[#0A0510]/40" />
              <div className="absolute inset-0 crt-scanlines opacity-50" />
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
                  ◉ Live signal
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/70">
                  REC <span className="ml-1 inline-block h-2 w-2 rounded-full bg-[#FF007A] flicker" />
                </p>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display text-3xl sm:text-5xl font-black neon-pink">
                  Around 30 to 50 machines.
                </p>
                <p className="mt-2 font-display text-xl sm:text-2xl text-white/80">
                  Both classic and modern.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 grid grid-cols-1 gap-6">
            <InsideCard
              mono="◉ Pull"
              title="Lights, sounds, and games that pull you in."
              body="And keep you there. Long after you said you would leave."
              accent="cyan"
              delay={80}
            />
            <InsideCard
              mono="◉ Free play"
              title="Unlimited play."
              body="So once you're in, you're in. No counting credits. No hesitation."
              accent="pink"
              delay={160}
            />
          </div>

          <InsideCard
            mono="◉ Slow corners"
            title="Spaces to sit back, chill, and take it all in."
            body="A mix of energy and comfort, all in one place."
            accent="purple"
            delay={120}
          />
          <div className="lg:col-span-7">
            <Reveal delay={200}>
              <div className="relative h-full p-8 sm:p-10 border border-white/10 bg-gradient-to-br from-[#1A0F26] to-[#0A0510]">
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A]">
                  ◉ &nbsp;Hidden
                </p>
                <p className="mt-5 font-display text-3xl sm:text-5xl font-black tracking-tighter">
                  And a few <span className="neon-cyan flicker-slow">surprises</span>
                  <br /> along the way.
                </p>
                <div className="mt-6 flex items-center gap-3 text-white/40">
                  <Sparkles className="h-4 w-4 text-[#00F0FF]" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.4em]">
                    You will know when you find them
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Games list strip */}
        <Reveal delay={120}>
          <div className="mt-10 border border-white/10 bg-[#0A0510]/60 backdrop-blur-sm p-7 sm:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-5">
              ◉ Inventory loading
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.32em]">
              {[
                { label: "Retro Cabinets", c: "pink" },
                { label: "Pinballs", c: "cyan" },
                { label: "Shooters", c: "purple" },
                { label: "Racing", c: "pink" },
                { label: "Claw", c: "cyan" },
                { label: "Basketball", c: "purple" },
                { label: "Air Hockey", c: "pink" },
                { label: "Consoles", c: "cyan" },
              ].map((g, i) => (
                <span
                  key={i}
                  className={`inline-flex items-center gap-2 border px-3.5 py-2 ${
                    g.c === "cyan"
                      ? "border-[#00F0FF]/40 text-[#00F0FF]"
                      : g.c === "purple"
                      ? "border-[#B026FF]/40 text-[#B026FF]"
                      : "border-[#FF007A]/40 text-[#FF007A]"
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current shadow-[0_0_6px_currentColor]" />
                  {g.label}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// Gallery ========================================================
const GalleryTile = ({ src, label, accent = "pink", caption, span = "", delay = 0 }) => {
  const accentClass =
    accent === "cyan"
      ? "border-[#00F0FF]/40 text-[#00F0FF]"
      : accent === "purple"
      ? "border-[#B026FF]/40 text-[#B026FF]"
      : "border-[#FF007A]/40 text-[#FF007A]";
  return (
    <Reveal delay={delay} className={span}>
      <div className={`group relative overflow-hidden border ${accentClass} aspect-[4/3] transition-shadow hover:shadow-[0_0_30px_rgba(255,0,122,0.3)]`}>
        <img
          src={src}
          alt={caption}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0510] via-[#0A0510]/30 to-transparent" />
        <div className="absolute inset-0 crt-scanlines opacity-30" />
        <div className="absolute top-3 left-3">
          <p className={`font-mono text-[10px] uppercase tracking-[0.4em] ${accentClass.replace('border-', 'text-').replace('/40', '')}`}>
            ◉ {label}
          </p>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="font-display text-base sm:text-lg font-bold text-white leading-tight">
            {caption}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

const Gallery = () => {
  return (
    <section
      id="gallery"
      className="relative px-6 md:px-12 py-24 md:py-32 overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-5">
                ◉ A glimpse inside
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[0.95]">
                Worlds <span className="neon-pink">within</span>
                <br />
                <span className="text-white/80">a single space.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={160}>
            <p className="max-w-sm text-[#C9C0D6] leading-relaxed">
              Pinball. Shooters. Rhythm. Claw. Lounge. Cocktails.
              <br />
              <span className="text-white/60">Every corner pulls you somewhere new.</span>
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-5">
          <GalleryTile src={IMG.PINBALL_BAR} label="Pinball + Bar" caption="Munsters pinball, cocktails close by." accent="purple" span="lg:col-span-3" />
          <GalleryTile src={IMG.LOUNGE} label="Game Zone" caption="Brick walls, neon, leather." accent="pink" span="lg:col-span-3" delay={80} />
          <GalleryTile src={IMG.CLAW} label="Sport + Claw" caption="Sonic basketball. The Claw waits." accent="cyan" span="lg:col-span-2" delay={140} />
          <GalleryTile src={IMG.RHYTHM} label="Rhythm" caption="Pump it. Move. Tetris glows." accent="pink" span="lg:col-span-2" delay={200} />
          <GalleryTile src={IMG.AIR_HOCKEY} label="Air Hockey" caption="Soft light. Real games." accent="purple" span="lg:col-span-2" delay={260} />
          <GalleryTile src={IMG.OLD_SCHOOL_SHOP} label="Old School" caption="VHS shelves. MTV checks. Mario watches." accent="cyan" span="lg:col-span-3" delay={120} />
          <GalleryTile src={IMG.TERMINATOR} label="Shooters" caption="Lock in. Aim. Reload." accent="pink" span="lg:col-span-3" delay={180} />
        </div>
      </div>
    </section>
  );
};

// Pricing =========================================================
const Pricing = () => {
  return (
    <section
      id="pricing"
      className="relative px-6 md:px-12 py-28 md:py-44 overflow-hidden"
      data-testid="pricing-section"
    >
      <div className="absolute inset-0 grid-bg opacity-20 radial-fade" />
      <div className="max-w-6xl mx-auto relative grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-6">
              ◉ 03 / Pricing
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-6xl sm:text-7xl md:text-8xl font-black tracking-tighter neon-pink leading-[0.9]">
              Simple.
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-8 space-y-4 text-xl sm:text-2xl font-display text-white">
              <p>Pay for your time.</p>
              <p className="neon-cyan">Play as much as you want.</p>
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 space-y-3 text-base sm:text-lg text-[#C9C0D6] leading-relaxed max-w-md">
              <p>No coins. No interruptions.</p>
              <p className="text-white">Just step in and enjoy it.</p>
            </div>
          </Reveal>
          <Reveal delay={400}>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.4em] text-white/60">
              Family options available.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={140}>
            <div className="relative">
              {/* Coin */}
              <div className="relative mx-auto w-[18rem] h-[18rem] sm:w-[22rem] sm:h-[22rem] rounded-full coin-chip border-[6px] border-[#FF007A] shadow-[0_0_60px_rgba(255,0,122,0.55),inset_0_0_40px_rgba(0,0,0,0.4)] flex items-center justify-center flicker-slow">
                <div className="absolute inset-3 rounded-full border border-white/30" />
                <div className="text-center">
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/80">
                    Replay
                  </p>
                  <p className="font-display text-7xl sm:text-8xl font-black text-white drop-shadow-[0_0_18px_rgba(255,255,255,0.5)]">
                    ∞
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/80">
                    Free play
                  </p>
                </div>
              </div>
              <div className="absolute -inset-10 -z-10 bg-[radial-gradient(ellipse,_rgba(255,0,122,0.35),_transparent_60%)]" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// Who It's For ====================================================
const WhoFor = () => {
  const lines = [
    { who: "Families", what: "looking for something different.", color: "pink" },
    { who: "Couples", what: "wanting a better kind of night out.", color: "cyan" },
    { who: "Groups, birthdays,", what: "and moments worth remembering.", color: "purple" },
    { who: "Anyone", what: "chasing that feeling again.", color: "pink" },
  ];
  return (
    <section
      id="who"
      className="relative px-6 md:px-12 py-28 md:py-40"
      data-testid="who-section"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A] mb-6">
            ◉ 04 / Who It&rsquo;s For
          </p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter">
            For anyone <span className="neon-cyan">in</span>.
          </h2>
        </Reveal>
        <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
          {lines.map((l, i) => (
            <Reveal delay={i * 90} key={i}>
              <div className="group flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8 py-7 sm:py-9 transition-colors hover:bg-white/[0.02]">
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40 sm:w-16 shrink-0">
                  0{i + 1}
                </p>
                <p className="font-display text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight">
                  <span className={
                    l.color === "cyan" ? "neon-cyan" : l.color === "purple" ? "neon-purple" : "neon-pink"
                  }>
                    {l.who}
                  </span>{" "}
                  <span className="text-white/85">{l.what}</span>
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// The Feeling =====================================================
const Feeling = () => {
  return (
    <section
      id="feeling"
      className="relative px-6 md:px-12 py-32 md:py-48 overflow-hidden"
      data-testid="feeling-section"
    >
      <div className="section-vignette" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(176,38,255,0.18),_transparent_60%)]" />
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-8">
            ◉ 05 / The Feeling
          </p>
        </Reveal>
        <Reveal delay={120}>
          <p className="font-display text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[1.05]">
            This isn&rsquo;t just somewhere you go.
          </p>
        </Reveal>

        <div className="mt-14 space-y-8 sm:space-y-10">
          <Reveal delay={80}>
            <p className="font-display text-2xl sm:text-4xl text-white">
              It&rsquo;s somewhere you <span className="neon-pink">talk about</span> after.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <p className="font-display text-2xl sm:text-4xl text-white">
              Somewhere that <span className="neon-cyan">sticks with you</span>.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <p className="font-display text-2xl sm:text-4xl text-white">
              Somewhere you <span className="neon-purple flicker-slow">come back to</span>.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// Location ========================================================
const Location = () => {
  return (
    <section
      id="location"
      className="relative px-6 md:px-12 py-28 md:py-40 overflow-hidden"
      data-testid="location-section"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#FF007A] mb-6">
              ◉ 06 / Location
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[0.95]">
              Based in <span className="neon-pink">Strabane</span>.
              <br />
              <span className="text-white/80">Open to surrounding areas.</span>
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-10 space-y-3 font-display text-2xl sm:text-3xl">
              <p className="text-white">Something new.</p>
              <p className="neon-cyan">Something different.</p>
              <p className="neon-purple">Right on your doorstep.</p>
            </div>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-10 inline-flex items-center gap-3 border border-white/15 px-4 py-3">
              <MapPin className="h-4 w-4 text-[#FF007A]" strokeWidth={1.5} />
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/80">
                Abercorn Square &nbsp;◉&nbsp; Strabane
              </span>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={150}>
            <div className="relative aspect-[4/5] sm:aspect-[5/4] overflow-hidden border border-white/10">
              <img
                src={IMG.STRABANE_BOOTH}
                alt="Booth with a view of Strabane"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0510] via-transparent to-[#FF007A]/20" />
              <div className="absolute inset-0 crt-scanlines opacity-35" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
                    Coordinates
                  </p>
                  <p className="font-display text-xl sm:text-2xl text-white mt-1">
                    54.83° N &nbsp;/&nbsp; 7.46° W
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/70">
                    Status
                  </p>
                  <p className="font-mono text-xs text-[#FF007A] mt-1 flicker">
                    ◉ COMING SOON
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// Final CTA =======================================================
const FinalCTA = ({ onPlan, onEnterArcade }) => {
  return (
    <section
      id="final"
      className="relative px-6 md:px-12 py-32 md:py-48 overflow-hidden"
      data-testid="final-section"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at center, rgba(255,0,122,0.4), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 grid-bg opacity-30 radial-fade" />
      <div className="relative max-w-5xl mx-auto text-center">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-8">
            ◉ The door is open
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h2
            data-text="Ready to step inside?"
            className="glitch-text font-display text-5xl sm:text-7xl md:text-8xl font-black tracking-tighter hero-glow text-white"
          >
            Ready to step <span className="neon-pink">inside?</span>
          </h2>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-5">
            <NeonButton variant="pink" onClick={onEnterArcade} data-testid="final-enter-arcade" className="pulse-glow">
              Enter Arcade
            </NeonButton>
            <NeonButton variant="cyan" onClick={onPlan} data-testid="final-plan-visit">
              Plan Your Visit
            </NeonButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// Footer ==========================================================
const Footer = () => (
  <footer className="relative px-6 md:px-12 py-14 border-t border-white/10" data-testid="footer">
    <div className="absolute inset-0 crt-grain pointer-events-none opacity-50" />
    <div className="max-w-7xl mx-auto flex flex-col gap-10">
      <div className="text-center">
        <p
          className="font-display text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter neon-pink calm-breath"
          data-testid="footer-signature"
        >
          Press Start on Real Life
        </p>
      </div>
      <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-5 pt-6 border-t border-white/5">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/5dx73wcq_IMG_8068.JPG"
            alt="Replay Arcade & Lounge"
            className="h-12 w-12 rounded-full object-cover shadow-[0_0_18px_rgba(176,38,255,0.55)] ring-1 ring-white/10 drift-subtle"
          />
          <div className="flex flex-col">
            <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/80">
              Replay Arcade <span className="text-[#FF007A]">&amp;</span> Lounge
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-white/40">
              Abercorn Square ◉ Strabane
            </span>
          </div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
          Press start. © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  </footer>
);

// Page wrapper ====================================================
const Home = () => {
  const [planOpen, setPlanOpen] = useState(false);
  const [entering, setEntering] = useState(false);
  const mainRef = useRef(null);

  // Listen to window-level event from any nested component if needed
  useEffect(() => {
    const open = () => setPlanOpen(true);
    window.addEventListener("replay:openPlan", open);
    return () => window.removeEventListener("replay:openPlan", open);
  }, []);

  const onPlan = () => setPlanOpen(true);

  const onEnterArcade = () => {
    if (entering) return;
    setEntering(true);
    // RGB split on the page, runs ~720ms in parallel with the overlay
    const main = mainRef.current;
    if (main) {
      main.classList.remove("enter-rgb");
      void main.offsetWidth;
      main.classList.add("enter-rgb");
      setTimeout(() => main.classList.remove("enter-rgb"), 740);
    }
    // At ~halfway through the transition, smoothly start scrolling to Experience
    setTimeout(() => {
      document.getElementById("experience")?.scrollIntoView({ behavior: "smooth" });
    }, 380);
  };

  const onContinue = () => {
    document.getElementById("experience")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main ref={mainRef} className="relative">
      <Hero onPlan={onPlan} onEnterArcade={onEnterArcade} onContinue={onContinue} />
      <StepInside onEnterArcade={onEnterArcade} />
      <Experience />
      <WhatsInside />
      <Gallery />
      <Pricing />
      <WhoFor />
      <Feeling />
      <Location />
      <FinalCTA onPlan={onPlan} onEnterArcade={onEnterArcade} />
      <Footer />
      <PlanVisitDialog open={planOpen} onOpenChange={setPlanOpen} />
      <EnterTransition active={entering} onComplete={() => setEntering(false)} />
    </main>
  );
};

export default Home;
